import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../../shared/api/api";
import AlertMessage from "../../../shared/components/AlertMessage";
import Modal from "../../../shared/components/Modal";
import PaginationToolbar from "../../../shared/components/PaginationToolbar";
import { useAutoDismissMessage } from "../../../shared/hooks/useAutoDismissMessage";
import { formatDate } from "../../../shared/utils/format";
import { calculateAge, crearPacienteFormulario, crearPacienteVacio, getBirthDate } from "../../../dominio/paciente";

const Pacientes = React.forwardRef(function Pacientes({ embedded = false, refreshKey = 0 }, ref) {
  const { apiFetch } = useApi();
  const [pacientes, setPacientes] = useState([]);
  const [form, setForm] = useState(crearPacienteVacio);
  const [editMode, setEditMode] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ visible: false, id: null, nombre: "" });
  const [paginaActual, setPaginaActual] = useState(1);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(5);
  const [busqueda, setBusqueda] = useState("");
  const [mostrarInactivos, setMostrarInactivos] = useState(true);
  const [mensaje, setMensaje] = useState(null);
  const [cargando, setCargando] = useState(false);
  const totalColumnas = embedded ? 8 : 9;
  const perfilBackState = embedded
    ? { from: "/administrar?tab=pacientes", label: "administracion de pacientes" }
    : { from: "/pacientes", label: "pacientes" };

  useAutoDismissMessage(mensaje, () => setMensaje(null));

  const listar = useCallback(async () => {
    setCargando(true);

    try {
      const data = await apiFetch("/pacientes.php?action=list");
      setPacientes(data || []);
      setPaginaActual(1);
    } finally {
      setCargando(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    listar();
  }, [listar, refreshKey]);

  // Filtrar pacientes por búsqueda
  const pacientesFiltrados = pacientes.filter((p) => {
    const textoBusqueda = busqueda.toLowerCase();
    const activo = Number(p.Estado) === 1;

    return (
      (mostrarInactivos || activo) &&
      (
        String(p.Nombre || "").toLowerCase().includes(textoBusqueda) ||
        String(p.EncargadoLegalNombreCompleto || "").toLowerCase().includes(textoBusqueda) ||
        String(p.Telefono || "").includes(busqueda) ||
        String(p.Email || "").toLowerCase().includes(textoBusqueda)
      )
    );
  });

  // Calcular paginación
  const totalPaginas = Math.ceil(pacientesFiltrados.length / registrosPorPagina);
  const indiceInicio = (paginaActual - 1) * registrosPorPagina;
  const pacientesPaginados = pacientesFiltrados.slice(indiceInicio, indiceInicio + registrosPorPagina);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const ejecutarGuardado = async () => {
    const estabaEditando = editMode;

    if (editMode) {
      await apiFetch("/pacientes.php?action=update", "POST", form);
    } else {
      await apiFetch("/pacientes.php?action=add", "POST", form);
    }

    setMensaje({
      tipo: "success",
      texto: estabaEditando ? "Paciente actualizado correctamente." : "Paciente agregado correctamente."
    });
    setConfirmSave(false);
    resetForm();
    setShowFormModal(false);
    listar();
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (editMode) {
      setConfirmSave(true);
      return;
    }

    await ejecutarGuardado();
  };

  const abrirModalAgregar = () => {
    resetForm();
    setMensaje(null);
    setShowFormModal(true);
  };

  React.useImperativeHandle(ref, () => ({
    abrirModalAgregar
  }));

  const editar = (p) => {
    setMensaje(null);
    setForm(crearPacienteFormulario(p));
    setEditMode(true);
    setShowFormModal(true);
  };

  const confirmarEliminar = (id, nombre) => {
    setConfirmDelete({ visible: true, id, nombre });
  };

  const cancelarEliminar = () => {
    setConfirmDelete({ visible: false, id: null, nombre: "" });
  };

  const eliminar = async () => {
    if (!confirmDelete.id) return;
    await apiFetch("/pacientes.php?action=delete", "POST", { IdPaciente: confirmDelete.id });
    cancelarEliminar();
    listar();
  };

  const resetForm = () => {
    setForm(crearPacienteVacio());
    setEditMode(false);
  };

  return (
    <div className={embedded ? "admin-embedded-section" : "container-fluid mt-3 px-3 px-md-4"}>
      {!embedded && <h2>Gestión de Pacientes</h2>}

      {!embedded && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Pacientes</h4>
          <button type="button" className="btn btn-success btn-sm" onClick={abrirModalAgregar}>Agregar paciente</button>
        </div>
      )}

      <AlertMessage message={mensaje} onClose={() => setMensaje(null)} />

      {/* Buscador */}
      <div className="card mb-3">
        <div className="card-body admin-filter-bar">
          <input
            type="text"
            className="form-control"
            placeholder="Buscar por paciente, encargado legal, teléfono o email..."
            value={busqueda}
            onChange={(e) => {
              setBusqueda(e.target.value);
              setPaginaActual(1);
            }}
          />
          <label className="form-check admin-inactive-toggle">
            <input
              className="form-check-input"
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => {
                setMostrarInactivos(e.target.checked);
                setPaginaActual(1);
              }}
            />
            <span className="form-check-label">Mostrar inactivos</span>
          </label>
        </div>
      </div>
 
      {/* Tabla */}
      <div className="card edge-to-edge-table-card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped table-sm mobile-compact-table patients-table">
              <thead className="table-dark">
                <tr>
                  {!embedded && <th>ID</th>}
                  <th>Nombre completo</th>
                  <th className="d-none d-lg-table-cell">Encargado legal</th>
                  <th className="d-none d-md-table-cell">Teléfono</th>
                  <th className="d-none d-xl-table-cell">Email</th>
                  <th className="d-none d-md-table-cell">Fecha de nacimiento</th>
                  <th className="d-none d-md-table-cell">Edad</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cargando && (
                  <tr>
                    <td colSpan={totalColumnas} className="text-center py-4">
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Cargando pacientes...
                    </td>
                  </tr>
                )}
                {!cargando && pacientesPaginados.map((p) => (
                  <tr key={p.IdPaciente}>
                    {!embedded && <td className="fw-bold">{p.IdPaciente}</td>}
                    <td className="text-truncate" title={p.Nombre}>{p.Nombre}</td>
                    <td className="d-none d-lg-table-cell text-truncate" title={p.EncargadoLegalNombreCompleto}>
                      {p.EncargadoLegalNombreCompleto || "N/A"}
                    </td>
                    <td className="d-none d-md-table-cell text-nowrap">{p.Telefono}</td>
                    <td className="d-none d-xl-table-cell text-truncate" title={p.Email}>{p.Email}</td>
                    <td className="d-none d-md-table-cell">{formatDate(getBirthDate(p))}</td>
                    <td className="d-none d-md-table-cell">{calculateAge(getBirthDate(p))}</td>
                    <td><span className={`badge bg-${p.Estado === 1 ? "success" : "secondary"}`}>{p.Estado === 1 ? "Activo" : "Inactivo"}</span></td>
                    <td>
                      <div className="patient-actions">
                        <Link
                          className="btn btn-outline-info btn-sm patient-table-action patient-profile-action"
                          title="Ver perfil"
                          aria-label="Ver perfil"
                          to={`/pacientes/${p.IdPaciente}/perfil`}
                          state={perfilBackState}
                        >
                          Perfil
                        </Link>
                        <div className="btn-group btn-group-sm table-row-actions" role="group" aria-label="Editar o eliminar paciente">
                          <button className="btn btn-primary" title="Editar paciente" onClick={() => editar(p)}>✎</button>
                          <button className="btn btn-danger" title="Eliminar paciente" onClick={() => confirmarEliminar(p.IdPaciente, p.Nombre)}>✕</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
                {!cargando && pacientes.length === 0 && <tr><td colSpan={totalColumnas} className="text-center">No hay pacientes</td></tr>}
                {!cargando && pacientes.length > 0 && pacientesFiltrados.length === 0 && <tr><td colSpan={totalColumnas} className="text-center">No se encontraron resultados para la búsqueda</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      
      <PaginationToolbar
        page={paginaActual}
        totalPages={totalPaginas}
        pageSize={registrosPorPagina}
        disabled={cargando}
        onPageChange={setPaginaActual}
        onPageSizeChange={(size) => {
          setRegistrosPorPagina(size);
          setPaginaActual(1);
        }}
      />


      {showFormModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editMode ? "Editar paciente" : "Agregar paciente"}</h5>
                <button type="button" className="btn-close" onClick={() => setShowFormModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={guardar} className="row g-2">
                  <div className="col-12 col-md-6">
                    <label className="form-label">Nombre completo del paciente</label>
                    <input type="text" name="Nombre" className="form-control" value={form.Nombre} onChange={handleChange} required />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Nombre completo del encargado legal</label>
                    <input
                      type="text"
                      name="EncargadoLegalNombreCompleto"
                      className="form-control"
                      value={form.EncargadoLegalNombreCompleto}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Teléfono</label>
                    <input type="text" name="Telefono" className="form-control" value={form.Telefono} onChange={handleChange} />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Email</label>
                    <input type="email" name="Email" className="form-control" value={form.Email} onChange={handleChange} />
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label">Fecha de nacimiento</label>
                    <input type="date" name="FechaNacimiento" className="form-control" value={form.FechaNacimiento} onChange={handleChange} />
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label">Estado</label>
                    <select name="Estado" className="form-select" value={form.Estado} onChange={handleChange}>
                      <option value="1">Activo</option>
                      <option value="0">Inactivo</option>
                    </select>
                  </div>
                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowFormModal(false)}>Cerrar</button>
                    <button type="submit" className="btn btn-success btn-sm">{editMode ? "Actualizar" : "Agregar"}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmSave && (
        <Modal
          title="Confirmar cambio"
          onClose={() => setConfirmSave(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmSave(false)}>Cancelar</button>
              <button type="button" className="btn btn-success" onClick={ejecutarGuardado}>Actualizar</button>
            </>
          }
        >
          <p>Deseas aplicar los cambios a este paciente?</p>
        </Modal>
      )}

      {confirmDelete.visible && (
        <Modal
          title="Confirmar eliminacion"
          onClose={cancelarEliminar}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={cancelarEliminar}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={eliminar}>Eliminar</button>
            </>
          }
        >
          <p>Estas seguro que quieres eliminar al paciente <strong>{confirmDelete.nombre}</strong>?</p>
        </Modal>
      )}
    </div>
  );
});

export default Pacientes;
